import React, { useEffect, useMemo, useRef } from 'react';
import RcTooltip from 'rc-tooltip';
import { Button, CircularProgress, Popover, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useTranslation } from 'react-i18next';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import {
  AUDIO_MODE,
  BLOCK_STATUS,
  CONVERTING_STATUS,
  EXPORT_POLL_FAILURE_BUDGET,
  EXPORT_STATUS,
  FETCH_EXPORT_STATUS_INTERVAL,
  REWRITE_STATUS,
} from '@src/constants/project';
import useTrackProjectExported from '@src/hooks/useTrackProjectExported';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { EXPORT_TYPE, VIDEO_EXPORT_ENGINE } from '@src/constants/presentation';
import {
  exportProject,
  getExportProgress,
  getProjectExportProgress,
} from '@src/apis/project';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { downloadFileByUrl, getFileName } from '@src/utils/file';
import { formatNumber } from '@src/utils/number';
import {
  audioExpired,
  calculateVideoExportCredits,
  checkBlocksChanged,
  handleDownloadParagraphsZip,
  isBlockChanged,
  sanitizeTitleFile,
} from '@src/services/project';
import apis from '@src/apis';
import FeedbackDialog from '@src/containers/ProjectDetail/component/FeedbackDialog';
import PopupNotEnoughCredits from '@src/containers/ProjectDetail/Dubbing/Content/PopupNotEnoughCredits';
import useFirstDownloadFeedback from '@src/hooks/useFirstDownloadFeedback';
import useTotalCredits from '@src/hooks/useTotalCredits';
import useExportAudio from '@src/hooks/useExportAudio';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import ExportRetentionNote from '../ExportPanel/ExportRetentionNote';
import ExportPanel from '../ExportPanel';
import {
  PopoverButton,
  StyledDownloadMenu,
  PopoverMenuItem,
} from './index.style';

const FILE_TYPE_OPTIONS = [
  { label: 'pptxFileType', value: EXPORT_TYPE.PPTX },
  { label: 'videoFileType', value: EXPORT_TYPE.VIDEO },
  { label: 'audioFile', value: 'audio' },
];

const STRUCTURE_OPTIONS = [
  { label: 'paragraphsZip', value: 'paragraphsZip' },
  { label: 'singleFile', value: 'singleFile' },
];

const DownloadButtonContent = ({ showLoading }) => {
  const { t } = useTranslation();
  const exportHistoryEnabled = useExportHistoryEnabled();
  if (showLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <CircularProgress
          value={75}
          className="circular-progress"
          size="16px"
        />
      </div>
    );
  }
  return t(exportHistoryEnabled ? 'exportFileAction' : 'download');
};

const PopoverDropdown = ({
  label,
  options,
  currentOption,
  onChangeOption,
  disabled,
  tParams,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = React.useState(null);

  const handleClick = (e) => {
    if (disabled) return;
    setAnchor(e.currentTarget);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const renderArrow = () => {
    if (anchor) {
      return <ArrowDropUpRoundedIcon width={24} height={24} />;
    }
    return <ArrowDropDownRoundedIcon width={24} height={24} />;
  };

  return (
    <div className="row">
      <div className="label">{label}</div>
      <PopoverButton active={!!anchor}>
        <Button
          className="custom-button"
          onClick={handleClick}
          disabled={disabled}
        >
          <span className="title-button">
            {t(currentOption?.label, tParams)}
          </span>
          {renderArrow()}
        </Button>
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          className="popover-structure"
          PaperProps={{
            style: { borderRadius: '8px' },
          }}
        >
          <div
            style={{
              padding: '8px 0px',
              border: '1px solid #1E164E52',
              borderRadius: '8px',
            }}
          >
            {options.map((item) => (
              <PopoverMenuItem
                onClick={() => {
                  onChangeOption(item);
                  setAnchor(null);
                }}
                active={currentOption?.value === item?.value}
                key={item.label}
              >
                <div className="wrap-label">
                  <Typography variant="body2" className="text">
                    {t(item.label, tParams)}
                  </Typography>
                  {currentOption?.value === item?.value && (
                    <CheckRoundedIcon sx={{ color: COLOR.darkBlue }} />
                  )}
                </div>
              </PopoverMenuItem>
            ))}
          </div>
        </Popover>
      </PopoverButton>
    </div>
  );
};

const OverlayComponent = ({
  handleDownload,
  fileTypeOptions,
  currentFileType,
  onChangeFileType,
  currentStructure,
  onChangeStructure,
  videoExportCredits,
  isExportBlocked,
  isDropdownBlocked,
  disabledByFileType,
  audioType,
  isGeneratingNarration,
}) => {
  const { t, i18n } = useTranslation();

  const fileTypeTParams = { format: audioType?.toLowerCase() };

  const isCurrentDownloadDisabled = disabledByFileType[currentFileType?.value];

  return (
    <StyledDownloadMenu>
      <PopoverDropdown
        label={
          <span className="animation-label">
            {t('fileTypeDownload')}
            {currentFileType?.value === EXPORT_TYPE.VIDEO && (
              <span className="animation-beta">{t('betaV2')}</span>
            )}
          </span>
        }
        options={fileTypeOptions}
        currentOption={currentFileType}
        onChangeOption={onChangeFileType}
        tParams={fileTypeTParams}
        disabled={isDropdownBlocked || isGeneratingNarration}
      />
      {currentFileType?.value === 'audio' && (
        <PopoverDropdown
          label={t('type')}
          options={STRUCTURE_OPTIONS}
          currentOption={currentStructure}
          onChangeOption={onChangeStructure}
          disabled={isDropdownBlocked || isGeneratingNarration}
        />
      )}
      {currentFileType?.value === EXPORT_TYPE.VIDEO && (
        <div className="credit-consumption">
          {t('creditConsumption', {
            credits: formatNumber(videoExportCredits, i18n.language),
          })}
        </div>
      )}
      <ExportRetentionNote />
      <Button
        className="download-btn"
        variant="contained"
        onClick={() => handleDownload()}
        disabled={
          isExportBlocked || isGeneratingNarration || isCurrentDownloadDisabled
        }
      >
        <DownloadButtonContent showLoading={isExportBlocked} />
      </Button>
    </StyledDownloadMenu>
  );
};

const PresentationDownloadButton = () => {
  const dispatch = useDispatch();
  const {
    multiBlock,
    title,
    isDownloading,
    audioType,
    originalAudioType,
    exports,
    currentFilteringStatus,
    currentFilteringVoiceCode,
    selectedBlockIds,
    isGeneratingNarration,
  } = useSelector((state) => state.project);
  const user = useSelector((state) => state.auth.user);

  const { getFeatureValue } = useFeatureFlags();
  const { totalCredits } = useTotalCredits();
  const trackProjectExport = useTrackProjectExported();

  const location = useLocation();
  const projectId = location.pathname.split('/')[2];

  const videoExportEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO,
    { userId: user?.id, email: user?.email },
  );

  // Feature flag gates the video option at the UI layer only.
  const fileTypeOptions = useMemo(
    () =>
      videoExportEnabled
        ? FILE_TYPE_OPTIONS
        : FILE_TYPE_OPTIONS.filter(
            (option) => option.value !== EXPORT_TYPE.VIDEO,
          ),
    [videoExportEnabled],
  );

  const [currentFileType, setCurrentFileType] = React.useState(
    FILE_TYPE_OPTIONS[0],
  );
  const [currentStructure, setCurrentStructure] = React.useState(
    STRUCTURE_OPTIONS[0],
  );
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  const closeTooltip = () => setIsTooltipOpen(false);
  const exportHistoryEnabled = useExportHistoryEnabled();
  const separateAudioExport = useExportAudio({
    projectId,
    exportType: EXPORT_TYPE.SEPARATE_AUDIO,
    onSuccess: closeTooltip,
  });

  const [initiatingType, setInitiatingType] = React.useState(null);
  const [openPopupNotEnoughCredits, setOpenPopupNotEnoughCredits] =
    React.useState(false);
  const exportIntervalsRef = useRef({});
  // Consecutive ticks that could not answer, per export type. A ref, not state:
  // the loop reads and writes it between renders, and a re-render here would
  // re-create the interval.
  const failureCountsRef = useRef({});

  const presentationProjectFeedbackEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_PROJECT_FEEDBACK,
    { userId: user?.id, email: user?.email },
  );

  const useDownloadSeparateAudioV2 = getFeatureValue(
    FEATURE_KEYS.USE_DOWNLOAD_SEPARATE_AUDIO_V2,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const {
    hasFirstDownloaded,
    markFirstDownloaded,
    openFeedback,
    setOpenFeedback,
  } = useFirstDownloadFeedback();

  const { blocks, originalBlocks, blockIds, originalBlockIds } =
    multiBlock || {};
  const blockWithsText = Object.values(blocks || {}).filter(
    (block) => block?.text,
  );

  const isRewriting = Object.values(blocks || {}).some(
    (block) => block?.rewrite?.status === REWRITE_STATUS.PENDING,
  );

  const isSynthesisInProgress = blockWithsText.some(
    (block) => block.status === CONVERTING_STATUS.IN_PROGRESS,
  );

  const hasAudioExpired = blockWithsText.some((block) =>
    audioExpired(block?.audioExpiredAt),
  );

  const blockWithTextChanged = useMemo(
    () =>
      blockWithsText.filter((block) => {
        const withoutExtension = block?.audioLink?.split('.')?.pop();
        const currentBlock = {
          text: block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          audioType: withoutExtension,
        };
        const originalBlock = {
          text: originalBlocks?.[block.id]?.text,
          voiceCode: originalBlocks?.[block.id]?.voiceCode,
          speed: originalBlocks?.[block.id]?.speed,
          audioType,
        };
        return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
      }),
    [blockWithsText, originalBlocks, audioType],
  );

  const isFilteringBlockStatus = useMemo(
    () => currentFilteringStatus !== BLOCK_STATUS.ALL,
    [currentFilteringStatus],
  );

  const isFilteringBlockVoice = useMemo(
    () => currentFilteringVoiceCode !== '',
    [currentFilteringVoiceCode],
  );

  const isDisabledDownload = useMemo(() => {
    if (isGeneratingNarration) return true;
    if (!blockWithsText?.length) return true;
    if (hasAudioExpired) return true;
    if (blockWithTextChanged?.length) return true;
    if (isSynthesisInProgress) return true;
    if (isFilteringBlockStatus) return true;
    if (isFilteringBlockVoice) return true;
    if (selectedBlockIds?.length) return true;
    return false;
  }, [
    blockWithsText,
    hasAudioExpired,
    blockWithTextChanged,
    isSynthesisInProgress,
    isFilteringBlockStatus,
    isFilteringBlockVoice,
    selectedBlockIds,
    isGeneratingNarration,
  ]);

  const isDisabledAudio = isDisabledDownload;

  const exportPptx = exports?.[EXPORT_TYPE.PPTX];
  const exportVideo = exports?.[EXPORT_TYPE.VIDEO];

  // A cached export can be reused only when nothing that re-renders its output
  // has changed since it was exported: audio type + block content/order. Notes
  // differ per type — pptx embeds slide notes, the video does not render them.
  const exportContentChanged = useMemo(() => {
    const isAudioTypeChanged = originalAudioType !== audioType;
    const base = { blocks, originalBlocks, blockIds, originalBlockIds };
    return {
      [EXPORT_TYPE.PPTX]: isAudioTypeChanged || checkBlocksChanged(base),
      [EXPORT_TYPE.VIDEO]:
        isAudioTypeChanged ||
        checkBlocksChanged({ ...base, includeNotes: false }),
    };
  }, [
    blocks,
    originalBlocks,
    blockIds,
    originalBlockIds,
    audioType,
    originalAudioType,
  ]);

  // Reuse iff the Backend still holds a fileUrl (it clears it on block change)
  // and nothing has changed on our side.
  // With the flag on every click is a new export, so a cached file is never
  // reused; with it off the previous behaviour stands.
  const canReuseExport = (exportData, exportType) => {
    if (exportHistoryEnabled) return false;
    if (!exportData?.fileUrl || exportContentChanged[exportType]) return false;
    // A cached video is reusable only when it was produced by the v2 engine
    // (the only engine now); an older v1 cache re-exports as v2.
    if (exportType === EXPORT_TYPE.VIDEO)
      return exportData?.videoExportEngine === VIDEO_EXPORT_ENGINE.V2;
    return true;
  };

  // Credits a video download will consume: 0 when the cached file is reused (no
  // BE export), else the fixed export cost. Only surfaced for the video option.
  const videoExportCredits = canReuseExport(exportVideo, EXPORT_TYPE.VIDEO)
    ? 0
    : calculateVideoExportCredits();

  const trackExport = (exportType, requestId) =>
    trackProjectExport({ exportType, requestId });

  const isPptxProcessing = exportPptx?.status === EXPORT_STATUS.PENDING;
  const isVideoProcessing = exportVideo?.status === EXPORT_STATUS.PENDING;

  // Who asks the backend about a type. The project-wide poll owns a type once
  // the record id is known; this component keeps its own loop for the rest: the
  // flag-off flow, and an export whose stored id is not a record id.
  const pollsHere = (exportType) =>
    !(exportHistoryEnabled && exports?.[exportType]?.requestId);
  // Last status seen here, per type. A record that is already SUCCESS when the
  // project opens is not a download that just finished.
  const prevStatusesRef = useRef({
    [EXPORT_TYPE.PPTX]: exportPptx?.status,
    [EXPORT_TYPE.VIDEO]: exportVideo?.status,
  });

  const isDisabledPptx = isDisabledDownload || isPptxProcessing;
  const isDisabledVideo = isDisabledDownload || isVideoProcessing;

  const isExportingPptx =
    initiatingType === EXPORT_TYPE.PPTX || isPptxProcessing;
  const isExportingVideo =
    initiatingType === EXPORT_TYPE.VIDEO || isVideoProcessing;
  const isExportingZip =
    initiatingType === EXPORT_TYPE.SEPARATE_AUDIO ||
    separateAudioExport.isExporting;
  const isLoading =
    isDownloading || isExportingPptx || isExportingVideo || isExportingZip;

  const isExportingByFileType = {
    [EXPORT_TYPE.PPTX]: isExportingPptx,
    [EXPORT_TYPE.VIDEO]: isExportingVideo,
    audio: isExportingZip,
  };

  const disabledByFileType = {
    [EXPORT_TYPE.PPTX]: isDisabledPptx,
    [EXPORT_TYPE.VIDEO]: isDisabledVideo,
    audio: isDisabledAudio,
  };

  // Flag on: one export per type at a time, so only the type already running is
  // blocked and the type dropdown stays usable. Flag off: any export in flight
  // blocks the whole popup, as before.
  const isExportBlocked = exportHistoryEnabled
    ? isDownloading || Boolean(isExportingByFileType[currentFileType?.value])
    : isLoading;
  const isDropdownBlocked = exportHistoryEnabled ? isDownloading : isLoading;

  useEffect(() => {
    if (isLoading) return;
    // Reset when the current type is hidden (not in the list) or disabled.
    const isCurrentSelectable =
      fileTypeOptions.some(
        (option) => option.value === currentFileType?.value,
      ) && !disabledByFileType[currentFileType?.value];
    if (isCurrentSelectable) return;

    const firstEnabled = fileTypeOptions.find(
      (option) => !disabledByFileType[option.value],
    );
    if (firstEnabled) setCurrentFileType(firstEnabled);
  }, [
    isDisabledPptx,
    isDisabledVideo,
    isDisabledAudio,
    isLoading,
    currentFileType,
    fileTypeOptions,
  ]);

  const isDisabledOuterButton = fileTypeOptions.every(
    (option) => disabledByFileType[option.value],
  );

  const stopExportPolling = (exportType, hasError = false) => {
    if (exportIntervalsRef.current[exportType]) {
      clearInterval(exportIntervalsRef.current[exportType]);
      exportIntervalsRef.current[exportType] = null;
    }
    if (hasError) {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'downloadError' }),
      );
    }
  };

  // A tick that cannot answer is not evidence the export failed: the record can
  // be missing for a moment, and a rejected envelope arrives as HTTP 200. Only a
  // spent budget ends the export, and it writes the final status, so no export
  // is left PENDING with nothing polling it.
  const failExportTick = (exportType) => {
    failureCountsRef.current[exportType] =
      (failureCountsRef.current[exportType] || 0) + 1;
    if (failureCountsRef.current[exportType] < EXPORT_POLL_FAILURE_BUDGET)
      return;

    stopExportPolling(exportType, true);
    dispatch(
      actions.project.setExportByType(exportType, {
        requestId: exports?.[exportType]?.requestId,
        status: EXPORT_STATUS.FAILURE,
      }),
    );
  };

  const startExportPolling = (exportType) => {
    if (exportIntervalsRef.current[exportType]) return;

    exportIntervalsRef.current[exportType] = setInterval(async () => {
      try {
        // With the record id known, ask about that exact record; the per-type
        // endpoint stays for exports started before the flow was switched on.
        const exportId = exports?.[exportType]?.requestId;
        const useRecordProgress = exportHistoryEnabled && exportId;
        const response = useRecordProgress
          ? await getProjectExportProgress(projectId, exportId)
          : await getExportProgress(projectId, exportType);
        // A rejected envelope (`status: 0`) arrives as HTTP 200 and carries no
        // status, so it cannot be read as progress — a record the backend
        // cannot find answers that way.
        if (!response?.status) {
          failExportTick(exportType);
          return;
        }
        failureCountsRef.current[exportType] = 0;

        // API responses are deep camelCased (see apis/api.js interceptor),
        // so server `file_url`/`request_id` arrive as `fileUrl`/`requestId`.
        const progress = useRecordProgress
          ? response?.result
          : response?.result?.progress;
        const { status, fileUrl, fileName } = progress || {};
        // The record endpoint answers with its own id; the per-type one calls
        // the same value `requestId`.
        const requestId = progress?.id || progress?.requestId;
        const exportData = {
          status,
          fileUrl,
          requestId,
          ...(exportType === EXPORT_TYPE.VIDEO && {
            videoExportEngine: VIDEO_EXPORT_ENGINE.V2,
          }),
        };

        if (status === EXPORT_STATUS.SUCCESS) {
          stopExportPolling(exportType);
          dispatch(actions.project.setExportByType(exportType, exportData));
          setIsTooltipOpen(false);
          trackExport(exportType, requestId);
          // S3 files are cross-origin, so the <a download> attribute is ignored
          // and inline-renderable types (e.g. mp4) open in the browser instead
          // of downloading. Fetch as a blob to force a same-origin download.
          if (fileUrl)
            downloadFileByUrl(fileUrl, fileName || getFileName(fileUrl));
        } else if (status === EXPORT_STATUS.FAILURE) {
          stopExportPolling(exportType, true);
          dispatch(actions.project.setExportByType(exportType, exportData));
        }
      } catch {
        failExportTick(exportType);
      }
    }, FETCH_EXPORT_STATUS_INTERVAL);
  };

  useEffect(() => {
    if (isPptxProcessing && pollsHere(EXPORT_TYPE.PPTX))
      startExportPolling(EXPORT_TYPE.PPTX);
    if (isVideoProcessing && pollsHere(EXPORT_TYPE.VIDEO))
      startExportPolling(EXPORT_TYPE.VIDEO);

    return () => {
      Object.keys(exportIntervalsRef.current).forEach((exportType) => {
        if (exportIntervalsRef.current[exportType]) {
          clearInterval(exportIntervalsRef.current[exportType]);
          exportIntervalsRef.current[exportType] = null;
        }
      });
    };
  }, [isPptxProcessing, isVideoProcessing]);

  // Download for the types the project-wide poll owns: it writes the status, and
  // the transition into SUCCESS is what a finished export looks like from here.
  useEffect(() => {
    [EXPORT_TYPE.PPTX, EXPORT_TYPE.VIDEO].forEach((exportType) => {
      const entry = exports?.[exportType];
      const status = entry?.status;
      const previous = prevStatusesRef.current[exportType];
      prevStatusesRef.current[exportType] = status;

      if (pollsHere(exportType)) return;
      if (previous !== EXPORT_STATUS.PENDING) return;
      // The failure toast belongs to the poll that spent the budget; a status
      // read out of redux cannot tell an already-announced failure from a new
      // one.
      if (status !== EXPORT_STATUS.SUCCESS) return;

      setIsTooltipOpen(false);
      trackExport(exportType, entry?.requestId);
      // S3 files are cross-origin, so the <a download> attribute is ignored and
      // inline-renderable types (e.g. mp4) open in the browser instead of
      // downloading. Fetch as a blob to force a same-origin download.
      const { fileUrl, fileName } = entry;
      if (fileUrl) downloadFileByUrl(fileUrl, fileName || getFileName(fileUrl));
    });
  }, [exportPptx, exportVideo]);

  const handleExport = async (exportType, videoExportEngine) => {
    if (isGeneratingNarration) return;
    setInitiatingType(exportType);
    failureCountsRef.current[exportType] = 0;
    try {
      const response = await exportProject(
        projectId,
        exportType,
        videoExportEngine,
      );
      const requestId = response?.result?.requestId || response?.result?.id;
      dispatch(
        actions.project.setExportByType(exportType, {
          requestId,
          status: EXPORT_STATUS.PENDING,
          ...(videoExportEngine && { videoExportEngine }),
        }),
      );
    } catch {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'downloadError' }),
      );
    } finally {
      setInitiatingType(null);
    }
  };

  // Reuse an already-exported file: no waiting on a fresh export.
  const handleDownloadExistingFile = (exportData, exportType) => {
    setIsTooltipOpen(false);
    trackExport(exportType);
    downloadFileByUrl(exportData.fileUrl, getFileName(exportData.fileUrl));
  };

  const handleDownloadMergedAudio = async () => {
    if (isGeneratingNarration) return;
    try {
      dispatch(actions.project.setIsDownloading(true));
      const response = await apis.project.getJoinedAudio(
        projectId,
        AUDIO_MODE.JOINED,
      );
      const audioJoined = response?.result?.audioLink;
      if (response.status && audioJoined) {
        const downloadAudioName = getFileName(audioJoined);
        trackExport(EXPORT_TYPE.JOIN_AUDIO);
        downloadFileByUrl(audioJoined, downloadAudioName);
      }
    } finally {
      setIsTooltipOpen(false);
      dispatch(actions.project.setIsDownloading(false));
    }
  };

  const handleDownloadIndividualAudioV1 = async () => {
    if (isGeneratingNarration) return;
    try {
      dispatch(actions.project.setIsDownloading(true));

      const audioLinks = blockWithsText.map((block) => ({
        audioLink: block.audioLink,
        title: block.text.substring(0, 30),
        requestId: block.requestId,
        newTitle: block.title || sanitizeTitleFile(block.text || ''),
      }));

      await handleDownloadParagraphsZip(audioLinks, title, audioType);
      trackExport(EXPORT_TYPE.SEPARATE_AUDIO);
    } finally {
      setIsTooltipOpen(false);
      dispatch(actions.project.setIsDownloading(false));
    }
  };

  const handleDownloadIndividualAudioV2 = async () => {
    if (isGeneratingNarration) return;

    // startExport automatically close tooltip when export success
    const { startExport } = separateAudioExport;
    await startExport();
  };

  const safeHandleDownloadIndividualAudio = async () => {
    if (useDownloadSeparateAudioV2) {
      handleDownloadIndividualAudioV2();
    } else {
      handleDownloadIndividualAudioV1();
    }
  };

  const executeDownload = ({ fileType, structure, videoExportEngine }) => {
    if (isGeneratingNarration) return undefined;
    switch (fileType) {
      case EXPORT_TYPE.PPTX:
        return canReuseExport(exportPptx, EXPORT_TYPE.PPTX)
          ? handleDownloadExistingFile(exportPptx, EXPORT_TYPE.PPTX)
          : handleExport(EXPORT_TYPE.PPTX);
      case EXPORT_TYPE.VIDEO: {
        if (canReuseExport(exportVideo, EXPORT_TYPE.VIDEO)) {
          return handleDownloadExistingFile(exportVideo, EXPORT_TYPE.VIDEO);
        }
        // Paid re-export: block and prompt to top up when the balance can't
        // cover the cost (mirrors the rewrite/translate credit flow).
        if (totalCredits < videoExportCredits) {
          setIsTooltipOpen(false);
          setOpenPopupNotEnoughCredits(true);
          return undefined;
        }
        return handleExport(EXPORT_TYPE.VIDEO, videoExportEngine);
      }
      case 'audio':
        return structure === 'singleFile'
          ? handleDownloadMergedAudio()
          : safeHandleDownloadIndividualAudio();
      default:
        return undefined;
    }
  };

  const handleDownload = () => {
    if (isGeneratingNarration) return;
    const downloadParams = {
      fileType: currentFileType?.value,
      structure: currentStructure?.value,
      videoExportEngine: VIDEO_EXPORT_ENGINE.V2,
    };

    if (presentationProjectFeedbackEnabled && !hasFirstDownloaded) {
      setOpenFeedback(true);
      executeDownload(downloadParams);
      markFirstDownloaded();
      return;
    }

    executeDownload(downloadParams);
  };

  const handleFeedbackClose = () => {
    setOpenFeedback(false);
  };

  const showLoadingButton = isLoading && !isTooltipOpen;

  return (
    <>
      <RcTooltip
        placement="bottomRight"
        overlay={
          <ExportPanel isOpen={isTooltipOpen}>
            <OverlayComponent
              fileTypeOptions={fileTypeOptions}
              currentFileType={currentFileType}
              onChangeFileType={setCurrentFileType}
              currentStructure={currentStructure}
              onChangeStructure={setCurrentStructure}
              videoExportCredits={videoExportCredits}
              handleDownload={handleDownload}
              isExportBlocked={isExportBlocked}
              isDropdownBlocked={isDropdownBlocked}
              disabledByFileType={disabledByFileType}
              audioType={audioType}
              isGeneratingNarration={isGeneratingNarration}
            />
          </ExportPanel>
        }
        trigger={['click']}
        showArrow={false}
        align={{
          offset: [0, 5],
        }}
        onVisibleChange={setIsTooltipOpen}
        visible={isTooltipOpen}
        destroyTooltipOnHide
      >
        <Button
          startIcon={
            !showLoadingButton && (
              <DownloadRoundedIcon style={{ fontSize: '24px' }} />
            )
          }
          className="download-btn"
          variant="contained"
          disabled={
            (isDisabledOuterButton || isRewriting || isGeneratingNarration) &&
            !exportHistoryEnabled
          }
          onClick={() => setIsTooltipOpen(!isTooltipOpen)}
        >
          <DownloadButtonContent showLoading={showLoadingButton} />
        </Button>
      </RcTooltip>
      <FeedbackDialog open={openFeedback} onClose={handleFeedbackClose} />
      <PopupNotEnoughCredits
        open={openPopupNotEnoughCredits}
        onClose={() => setOpenPopupNotEnoughCredits(false)}
        projectId={projectId}
      />
    </>
  );
};

export default PresentationDownloadButton;
